import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <div className="relative bg-white">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-gray-100 opacity-50">
          <div className="absolute inset-0" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}></div>
        </div>
        
        <div className="relative py-12 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Main Content */}
            <div className={`${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} transition-all duration-1000 ease-out`}>
              {/* Badge */}
              <div className="inline-flex items-center mb-6 px-4 py-2 rounded-md text-sm font-bold bg-blue-50 border-2 border-blue-200 text-blue-700 shadow-sm">
                <span className="mr-2 h-3 w-3 rounded-full bg-blue-500"></span>
                White Paper Now Available • Breaking Down Career Barriers
              </div>
              
              {/* Main Headline */}
              <h1 className="text-5xl sm:text-6xl font-black tracking-tight text-gray-900 mb-6 leading-tight">
                A Win-Win Partnership
                <span className="block text-primary-600 mt-2">For People and Business</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-4 max-w-2xl">
                StaffAbility proposes a revolutionary approach that transforms lives through career opportunity while providing businesses with exceptional talent, higher retention, and measurable ROI.
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200 text-sm font-medium">83% retention rate</span>
                <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 text-sm font-medium">91% attendance rate</span>
                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-200 text-sm font-medium">$9,600 tax credits</span>
                <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-200 text-sm font-medium">12.5x ROI</span>
              </div>
              
              <div className="flex flex-wrap gap-4 mb-12">
                <Link href="/about">
                  <Button size="lg" className="bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-md">
                    Explore Our Vision <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                
                <Link href="/programs">
                  <Button size="lg" variant="outline" className="border-slate-300 bg-white text-slate-800 hover:bg-slate-100 shadow-md">
                    View Programs
                  </Button>
                </Link>
                
                <Link href="/hire-from-us">
                  <Button size="lg" variant="outline" className="border-slate-300 bg-white text-slate-800 hover:bg-slate-100 shadow-md">
                    Hire Qualified Talent
                  </Button>
                </Link>
              </div>
              
              {/* Stats Section */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-gray-200 pt-8">
                <div>
                  <div className="text-3xl font-bold text-primary-600 mb-1">100%</div>
                  <div className="text-gray-600 text-sm">Target employment</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-600 mb-1">24mo</div>
                  <div className="text-gray-600 text-sm">Support period</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-600 mb-1">100%</div>
                  <div className="text-gray-600 text-sm">Recidivism elimination</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-600 mb-1">12.5x</div>
                  <div className="text-gray-600 text-sm">ROI over 5 years</div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Features Box */}
            <div className={`${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} transition-all duration-1000 ease-out delay-300`}>
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-white px-6 py-4 border-b border-slate-200">
                  <h3 className="font-bold text-xl text-slate-900">Dual-Impact Model</h3>
                </div>
                <div className="p-6">
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full mb-2">For Businesses</span>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="h-3 w-3 rounded-full bg-blue-600"></div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Exceptional Talent Pipeline</h4>
                          <p className="text-gray-600 text-sm mt-1">Pre-screened, highly motivated employees with 83% retention vs. 59% industry average.</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="h-3 w-3 rounded-full bg-green-600"></div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Financial Incentives</h4>
                          <p className="text-gray-600 text-sm mt-1">Tax credits up to $9,600 per hire plus $5,200 average savings in hiring and training costs.</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mb-2 border-t border-gray-100 pt-4">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full mb-2">For Participants</span>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="h-3 w-3 rounded-full bg-amber-600"></div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Skills & Career Pathways</h4>
                          <p className="text-gray-600 text-sm mt-1">Industry-recognized certifications in technology, trades, and logistics with upward mobility.</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="h-3 w-3 rounded-full bg-purple-600"></div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Comprehensive Support</h4>
                          <p className="text-gray-600 text-sm mt-1">24-month ongoing support including housing, counseling, and mentorship for long-term success.</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center mb-2">
                      <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded mr-2 uppercase tracking-wider font-bold">New</span>
                      <h4 className="font-semibold text-blue-900">
                        Career Coach AI
                      </h4>
                    </div>
                    <p className="text-blue-700 text-sm mb-3">
                      Get personalized guidance about our programs through our AI assistant.
                    </p>
                    <Link href="/career-coach">
                      <Button variant="outline" className="border-slate-300 bg-white text-slate-800 hover:bg-slate-100 shadow-md w-full mt-2">
                        Try it now <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
          <a href="#overview" className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow">
            <ChevronDown className="h-6 w-6 text-primary-600" />
          </a>
        </div>
      </div>

      {/* Overview Section */}
      <div id="overview" className="bg-gray-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <div className="bg-primary-100 rounded-lg p-2 text-primary-700 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5zM18 1.5a.75.75 0 01.728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 010 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 01-1.456 0l-.258-1.036a2.625 2.625 0 00-1.91-1.91l-1.036-.258a.75.75 0 010-1.456l1.036-.258a2.625 2.625 0 001.91-1.91l.258-1.036A.75.75 0 0118 1.5zM16.5 15a.75.75 0 01.712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 010 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 01-1.422 0l-.395-1.183a1.5 1.5 0 00-.948-.948l-1.183-.395a.75.75 0 010-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0116.5 15z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-bold bg-primary-100 text-primary-700 tracking-wider uppercase border border-primary-200">
                  Our Vision
                </div>
              </div>
              <h3 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                A Revolutionary Approach to Justice Reform
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Our <Link to="/research" className="text-primary-600 hover:text-primary-800 font-medium underline">white paper</Link> presents a pioneering model that addresses both workforce gaps and systemic barriers to reentry. StaffAbility envisions a 24-month intensive support program that transforms lives through:
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 border border-primary-200">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">Skills-First Training</h4>
                    <p className="text-gray-600">
                      Specialized technical training mapped to high-demand industries with vetted employer partners.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 border border-primary-200">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">Wraparound Support</h4>
                    <p className="text-gray-600">
                      Integrated housing, counseling, and mentorship that addresses all barriers to employment success.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 border border-primary-200">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">Strategic Employer Partnerships</h4>
                    <p className="text-gray-600">
                      Two-way relationships where businesses gain loyal employees with strong work ethic while making social impact.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-primary-600 mr-2">
                      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
                    </svg>
                    Why Employers Choose StaffAbility
                  </h4>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-green-500 mr-2 flex-shrink-0">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      <span>83% retention rate (vs. 59% industry average)</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-green-500 mr-2 flex-shrink-0">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      <span>Tax credits up to $9,600 per eligible hire</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-green-500 mr-2 flex-shrink-0">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      <span>91% attendance rate and exceptional work ethic</span>
                    </li>
                  </ul>
                </div>
                
                <blockquote className="border-l-4 border-primary-500 pl-4">
                  <p className="text-lg text-gray-700 italic mb-4 leading-relaxed">
                    "Our model creates <span className="font-bold text-primary-700">win-win partnerships</span> where justice-involved individuals gain career paths and businesses gain exceptional employees with <span className="font-bold text-primary-700">proven performance benefits</span>."
                  </p>
                  <footer className="text-gray-600">
                    <div className="font-semibold text-gray-900">Dr. Jane Doe</div>
                    <div className="text-sm">Lead Researcher</div>
                  </footer>
                </blockquote>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-primary-100 rounded-lg p-2 text-primary-700 mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">
                      Proposed Impact by 2030
                    </h4>
                  </div>

                  <p className="text-center text-slate-600 mb-5 max-w-md mx-auto">
                    Our long-term vision will create significant societal and economic transformation.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex items-center gap-3">
                      <div className="bg-blue-100 rounded-md p-2 text-blue-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-blue-700 font-bold text-xl">50,000+</div>
                        <div className="text-slate-700 text-sm">Program graduates</div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex items-center gap-3">
                      <div className="bg-green-100 rounded-md p-2 text-green-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z" />
                          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-green-700 font-bold text-xl">$720M</div>
                        <div className="text-slate-700 text-sm">Economic impact</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}